import { useState } from "react";
import { ChevronDown, ListChecks, CheckCircle2, AlertCircle } from "@/lib/icons";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PROJECT_TOPICS } from "@/data/endOfYearProject";

interface TeksRow {
  code: string;
  title: string;
  topicIds: string[];
}

const TEKS_MAP: TeksRow[] = [
  {
    code: "§112.35(c)(1)-(3)",
    title: "Lab safety, scientific methods, critical thinking",
    topicIds: ["lab-foundations"],
  },
  {
    code: "§112.35(c)(4)",
    title: "Use of equipment, units, sig figs, graphing",
    topicIds: ["lab-foundations", "stoichiometry", "gas-laws"],
  },
  {
    code: "§112.35(c)(5)",
    title: "Atomic structure, electron configuration",
    topicIds: ["atomic-structure"],
  },
  {
    code: "§112.35(c)(6)",
    title: "Periodic table organization & trends",
    topicIds: ["periodic-table"],
  },
  {
    code: "§112.35(c)(7)",
    title: "Bonding, Lewis structures, IMFs, VSEPR",
    topicIds: ["bonding", "atomic-structure"],
  },
  {
    code: "§112.35(c)(8)",
    title: "Nomenclature, formulas, balanced equations",
    topicIds: ["bonding", "stoichiometry"],
  },
  {
    code: "§112.35(c)(9)-(10)",
    title: "Stoichiometry, limiting reactant, percent yield",
    topicIds: ["stoichiometry"],
  },
  {
    code: "§112.35(c)(10)(I)",
    title: "Quantitative titration & molarity",
    topicIds: ["acid-base"],
  },
  {
    code: "§112.35(c)(11)",
    title: "Gas laws, KMT, PV=nRT, Dalton's Law",
    topicIds: ["gas-laws"],
  },
  {
    code: "§112.35(c)(12)",
    title: "Solutions, solubility, concentration",
    topicIds: ["solutions"],
  },
  {
    code: "§112.35(c)(13)",
    title: "Acid-base chemistry & pH",
    topicIds: ["acid-base"],
  },
  {
    code: "§112.35(c)(14)",
    title: "Thermochemistry, calorimetry, Hess's Law",
    topicIds: ["thermochemistry"],
  },
  {
    code: "§112.35(c)(15)",
    title: "Nuclear chemistry, decay, half-life",
    topicIds: ["nuclear"],
  },
];

export default function TeksCoverageMap() {
  const [open, setOpen] = useState(false);
  const topicById = new Map(PROJECT_TOPICS.map((t) => [t.id, t]));
  const validIds = new Set(PROJECT_TOPICS.map((t) => t.id));
  const rowsWithStatus = TEKS_MAP.map((r) => ({
    ...r,
    covered: r.topicIds.some((id) => validIds.has(id)),
  }));
  const coveredCount = rowsWithStatus.filter((r) => r.covered).length;
  const totalCount = rowsWithStatus.length;
  const missing = rowsWithStatus.filter((r) => !r.covered);
  const pct = Math.round((coveredCount / totalCount) * 100);

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors rounded-t-lg"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <ListChecks size={18} className="text-primary" aria-hidden="true" />
          <div>
            <div className="text-sm font-semibold text-foreground">TEKS Coverage Map</div>
            <div className="text-xs text-muted-foreground">
              Show district reviewers exactly where each Student Expectation lives.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge
            variant={missing.length === 0 ? "secondary" : "outline"}
            className="text-[10px] font-mono"
          >
            {coveredCount}/{totalCount} covered
          </Badge>
          <ChevronDown
            size={16}
            className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </div>
      </button>
      {open && (
        <CardContent className="pt-0">
          {/* Coverage completeness checklist */}
          <div className="mb-4 rounded-md border border-border bg-muted/20 p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {missing.length === 0 ? (
                  <CheckCircle2 size={16} className="text-primary" aria-hidden="true" />
                ) : (
                  <AlertCircle size={16} className="text-amber-500" aria-hidden="true" />
                )}
                <span className="text-sm font-semibold text-foreground">
                  Coverage completeness
                </span>
              </div>
              <span className="font-mono text-xs text-muted-foreground">{pct}%</span>
            </div>
            <div
              className="h-1.5 w-full rounded-full bg-border overflow-hidden"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {rowsWithStatus.map((r) => (
                <li key={r.code} className="flex items-start gap-2">
                  {r.covered ? (
                    <CheckCircle2
                      size={12}
                      className="text-primary mt-0.5 flex-shrink-0"
                      aria-hidden="true"
                    />
                  ) : (
                    <AlertCircle
                      size={12}
                      className="text-amber-500 mt-0.5 flex-shrink-0"
                      aria-hidden="true"
                    />
                  )}
                  <span
                    className={
                      r.covered ? "text-muted-foreground" : "text-foreground font-medium"
                    }
                  >
                    <span className="font-mono">{r.code}</span> - {r.title}
                    {!r.covered && (
                      <span className="ml-1 text-amber-500">(no pathway)</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            {missing.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {missing.length} expectation{missing.length === 1 ? "" : "s"} need a pathway
                before this project fully covers Texas Chemistry §112.35(c).
              </p>
            )}
          </div>

          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left font-semibold px-3 py-2 w-[180px]">TEKS</th>
                  <th className="text-left font-semibold px-3 py-2">Student Expectation</th>
                  <th className="text-left font-semibold px-3 py-2">Covered by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rowsWithStatus.map((row) => (
                  <tr key={row.code} className="align-top">
                    <td className="px-3 py-2 font-mono text-xs text-foreground whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        {row.covered ? (
                          <CheckCircle2 size={12} className="text-primary" aria-hidden="true" />
                        ) : (
                          <AlertCircle size={12} className="text-amber-500" aria-hidden="true" />
                        )}
                        {row.code}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{row.title}</td>
                    <td className="px-3 py-2">
                      {row.covered ? (
                        <div className="flex flex-wrap gap-1">
                          {row.topicIds.map((id) => {
                            const t = topicById.get(id);
                            if (!t) return null;
                            return (
                              <Badge
                                key={id}
                                variant="outline"
                                className="text-[10px] font-normal"
                              >
                                {String(t.number).padStart(2, "0")} · {t.title}
                              </Badge>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-xs text-amber-500">Not covered</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.print()}
              className="text-xs"
            >
              Print coverage map
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}